// API de las PLANTILLAS PROPIAS del modo "Replicar una foto" — las fotos de
// referencia que sube el usuario para que Atlas les meta su producto adentro.
// Tabla `plantillas_propias` (RLS por empresa, migración 20260910130000);
// archivos en el bucket privado `modelos`, path
// `<id_company>/plantillas_propias/<uuid>.<ext>` → signed URLs para mostrar y
// para mandar como referencia de generación.
// Ver ADR-2026-09-10-modo-replicar-foto-belleza.
//
// Calca la forma de `posesCustomApi.ts` (el otro "asset propio, subido,
// coleccionado y borrable") con DOS diferencias:
//
//   • SIN BACKEND. Una pose subida pasa por `/v1/models/process-pose`, que
//     devuelve el muñeco-en-pose; acá la imagen se sube tal cual: la plantilla
//     ES la foto. Subir es gratis y no hay job que esperar.
//   • `filename` guarda el PATH COMPLETO dentro del bucket, no sólo el nombre.
//     En poses el path se recompone en cada lectura (`<company>/poses_custom/` +
//     filename), así que la convención de carpetas vive en dos lados; acá la fila
//     dice dónde está el archivo y listo.
//
// Por qué el bucket `modelos` y no uno nuevo: su policy ("Gestionar modelos de la
// propia compañia") ya da ALL sobre todo lo que empieza con `<id_company>/`, que
// es exactamente lo que hace falta. Es donde ya viven las poses propias, que
// tampoco son modelos.

import { supabase } from '../../lib/supabase';
import { normalizeImageForUpload } from '../../utils/heicConvert';

const BUCKET = 'modelos';
/** Dónde viven las plantillas: lo necesita el nivel alto del modo, que le manda al backend
 *  bucket + path para EDITAR la foto (ADR-2026-09-13-replicar-una-foto-tres-pipelines). */
export const PLANTILLAS_BUCKET = BUCKET;
const SIGNED_TTL = 3600;

/** La carpeta de la empresa donde viven sus plantillas. */
const carpeta = (idCompany: string) => `${idCompany}/plantillas_propias`;

export interface PlantillaPropia {
  id: string;
  /** "Plantilla 1", "Plantilla 2"… */
  nombre: string;
  /** Path completo dentro del bucket `modelos`. Lo que se re-firma al generar. */
  path: string;
  /** Signed URL para la grilla. Vence: al generar se re-firma con `firmarPlantilla`. */
  imageUrl: string;
  createdAt: string;
}

/** Nombre genérico de las plantillas subidas — mismo criterio que
 *  `nombrePosePropia`: un contador, no un campo que el usuario escriba. */
export const nombrePlantillaPropia = (n: number) => `Plantilla ${n}`;

/**
 * Las plantillas de la empresa, la última primero, con signed URL para mostrar.
 *
 * Las firma en UNA llamada (`createSignedUrls` en plural) y no una por una como
 * `fetchMyCustomPoses`: la grilla las pide todas juntas y con 20 plantillas eso
 * son 20 round-trips antes del primer pixel.
 */
export async function fetchPlantillasPropias(
  idCompany: string,
): Promise<PlantillaPropia[]> {
  const { data, error } = await supabase
    .from('plantillas_propias')
    .select('id, nombre, filename, created_at')
    .eq('id_company', idCompany)
    .order('created_at', { ascending: false });
  if (error || !data) {
    if (error) console.error('[plantillasPropiasApi] fetch error', error);
    return [];
  }
  const rows = data as Array<{
    id: string;
    nombre: string | null;
    filename: string;
    created_at: string;
  }>;
  if (rows.length === 0) return [];

  const { data: firmadas } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(
      rows.map((r) => r.filename),
      SIGNED_TTL,
    );
  // La respuesta viene en el mismo orden que los paths pedidos, pero indexamos
  // por path para no depender de eso.
  const urlPorPath = new Map<string, string>();
  for (const f of firmadas || []) {
    if (f.path && f.signedUrl) urlPorPath.set(f.path, f.signedUrl);
  }

  return rows.map((r, i) => ({
    id: String(r.id),
    // El fallback numera por orden de creación (la query viene DESC, por eso el
    // índice se da vuelta), igual que en las poses propias.
    nombre: (r.nombre || '').trim() || nombrePlantillaPropia(rows.length - i),
    path: r.filename,
    imageUrl: urlPorPath.get(r.filename) || '',
    createdAt: r.created_at,
  }));
}

/**
 * Sube una plantilla y la deja guardada en la colección.
 *
 * `normalizeImageForUpload` es lo que hace que una foto sacada con un iPhone
 * (HEIC) funcione, y lo que tira `UnsupportedImageFormatError` con un mensaje
 * mostrable cuando el archivo es un RAW de cámara. El caller lo deja subir.
 *
 * `brandId` se GUARDA pero nadie lo lee (la colección es de la empresa): es el
 * único momento en que se sabe cuál era la marca activa — ver el ADR.
 */
export async function subirPlantillaPropia(opts: {
  file: File;
  idCompany: string;
  brandId?: string | null;
  /** Cuántas plantillas ya tiene la empresa — para numerar la nueva. */
  cantidadActual: number;
}): Promise<PlantillaPropia> {
  const { file, idCompany, brandId = null, cantidadActual } = opts;

  const normalizado = await normalizeImageForUpload(file);
  const ext = (
    normalizado.name.match(/\.([a-zA-Z0-9]+)$/)?.[1] || 'jpg'
  ).toLowerCase();
  const path = `${carpeta(idCompany)}/${crypto.randomUUID()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, normalizado, {
      contentType: normalizado.type || undefined,
      upsert: false,
    });
  if (upErr) throw new Error(`No se pudo subir la plantilla: ${upErr.message}`);

  const nombre = nombrePlantillaPropia(cantidadActual + 1);
  const { data, error } = await supabase
    .from('plantillas_propias')
    .insert({
      id_company: idCompany,
      brand_id: brandId,
      nombre,
      filename: path,
    })
    .select('id, created_at')
    .single();
  if (error || !data) {
    // El archivo ya está arriba y la fila no: sin fila no se lista ni se borra
    // nunca más, así que lo sacamos antes de fallar.
    void supabase.storage.from(BUCKET).remove([path]);
    throw new Error(
      `No se pudo guardar la plantilla: ${error?.message || 'sin detalle'}`,
    );
  }

  const imageUrl = (await firmarPlantilla(path)) || '';
  return {
    id: String((data as { id: string }).id),
    nombre,
    path,
    imageUrl,
    createdAt: (data as { created_at: string }).created_at,
  };
}

/** Re-firma el path de una plantilla. Se llama al GENERAR: la URL que trajo la
 *  grilla puede llevar una hora en pantalla y `reference_images` necesita una
 *  URL que el backend pueda descargar. */
export async function firmarPlantilla(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_TTL);
  if (error || !data?.signedUrl) {
    if (error) console.error('[plantillasPropiasApi] firmar error', error);
    return null;
  }
  return data.signedUrl;
}

/**
 * Borra la plantilla: el archivo y la fila.
 *
 * El archivo primero y best-effort (como en las poses): si el remove falla queda
 * un huérfano en storage, molesto pero invisible; si fallara al revés quedaría
 * una fila en la grilla apuntando a nada.
 *
 * Las imágenes YA generadas con esta plantilla no se tocan — viven en
 * `imagenes_generadas` y no la referencian.
 */
export async function borrarPlantillaPropia(opts: {
  id: string;
  path: string;
  idCompany: string;
}): Promise<void> {
  const { id, path, idCompany } = opts;
  await supabase.storage.from(BUCKET).remove([path]);
  const { error } = await supabase
    .from('plantillas_propias')
    .delete()
    .eq('id', id)
    .eq('id_company', idCompany);
  if (error) throw new Error(`No se pudo borrar la plantilla: ${error.message}`);
}
